import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { addToast } from 'containers/Toasts/actions'

import {
  addWhitelistError,
  addWhitelistSuccess,
  deleteSelectedWhitelistsError,
  deleteSelectedWhitelistsSuccess,
  editWhitelistError,
  editWhitelistSuccess,
  getWhitelistedError,
  getWhitelistedSuccess,
} from './actions'
import {
  BY_COMPANY_SORT_PROPERTY_CHANGE,
  BY_COMPANY_TYPE_FILTER_CHANGE,
  WHITELIST_ADD_ASYNC,
  WHITELIST_DELETE_SELECTED_ASYNC,
  WHITELIST_EDIT_ASYNC,
  WHITELIST_GET_ASYNC,
  WHITELIST_PAGE_CHANGE,
  WHITELIST_SEARCH_FILTER_CHANGE,
} from './constants'
import { selectSelectedIds } from './selectors'

const getWhitelistedEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      WHITELIST_GET_ASYNC.INITIATE,
      WHITELIST_ADD_ASYNC.SUCCESS,
      WHITELIST_EDIT_ASYNC.SUCCESS,
      BY_COMPANY_TYPE_FILTER_CHANGE,
      BY_COMPANY_SORT_PROPERTY_CHANGE,
      WHITELIST_PAGE_CHANGE,
      WHITELIST_SEARCH_FILTER_CHANGE,
      WHITELIST_DELETE_SELECTED_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.allowlist)
      return true
    }),
    mergeMap(() =>
      api.settings
        .getWhitelisted(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.settings.whitelist.self.byCompanyTypeFilter,
          state$.value.settings.whitelist.self.byCompanySortProperty,
          state$.value.settings.whitelist.self.search,
          state$.value.settings.whitelist.self.page,
          1000 // itemsPerPage -- temp until real pagination
        )
        .pipe(
          map((response) => getWhitelistedSuccess(response)),
          catchError((error) => of(epicsErrorHandler(getWhitelistedError, error))),
          takeUntil(action$.pipe(ofType(WHITELIST_GET_ASYNC.CANCEL)))
        )
    )
  )

const deleteSelectedWhitelistsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WHITELIST_DELETE_SELECTED_ASYNC.INITIATE),
    mergeMap((action) => {
      const { ids } = action.payload
      // a bare onClick={deleteSelected} would put React's event here
      const selectedIds = Array.isArray(ids) ? ids : selectSelectedIds(state$.value)
      return api.settings
        .deleteSelectedWhitelists(state$.value.partnerOptions.selectedCompany?.value, selectedIds)
        .pipe(
          map(() => deleteSelectedWhitelistsSuccess(selectedIds)),
          catchError((error) => of(epicsErrorHandler(deleteSelectedWhitelistsError, error))),
          takeUntil(action$.pipe(ofType(WHITELIST_DELETE_SELECTED_ASYNC.CANCEL)))
        )
    })
  )

const addWhitelistEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WHITELIST_ADD_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .addWhitelist(state$.value.partnerOptions.selectedCompany?.value, action.payload.data)
        .pipe(
          switchMap((response) =>
            of(
              addToast({ message: 'Allowed item was successfully added' }),
              addWhitelistSuccess(response)
            )
          ),
          catchError((error) => {
            const errorMessage =
              error.response?.data?.message ||
              error.response?.message ||
              error.message ||
              'Failed to add allowed item'
            return of(
              addToast({ message: errorMessage, type: 'error' }),
              epicsErrorHandler(addWhitelistError, error)
            )
          }),
          takeUntil(action$.pipe(ofType(WHITELIST_ADD_ASYNC.CANCEL)))
        )
    )
  )

const editWhitelistEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WHITELIST_EDIT_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .editWhitelist(state$.value.partnerOptions.selectedCompany?.value, action.payload.data)
        .pipe(
          switchMap((response) =>
            of(
              addToast({ message: 'Allowed item was successfully edited' }),
              editWhitelistSuccess(response)
            )
          ),
          catchError((error) => of(epicsErrorHandler(editWhitelistError, error))),
          takeUntil(action$.pipe(ofType(WHITELIST_EDIT_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  getWhitelistedEpic,
  deleteSelectedWhitelistsEpic,
  addWhitelistEpic,
  editWhitelistEpic
)
